import {
  CommonModelDiffRunExecution,
  CommonModelDiffRunStatus,
  DiffCategoryTypes,
  DiffExecutionModelResult,
} from "./entities";

export const sortDiffRunsByStatus = (
  diffRuns: CommonModelDiffRunExecution[]
): CommonModelDiffRunExecution[] => {
  const getPriority = (status: CommonModelDiffRunStatus): number => {
    switch (status) {
      case CommonModelDiffRunStatus.SUCCEEDED:
      case CommonModelDiffRunStatus.FAILED:
        return 0;
      case CommonModelDiffRunStatus.IN_PROGRESS:
        return 1;
      case CommonModelDiffRunStatus.PENDING:
        return 2;
      default:
        return 3;
    }
  };

  // Returns a sorted array based on status and then ID if the status is the same
  return [...diffRuns].sort((a, b) => {
    const aPriority = getPriority(a.status);
    const bPriority = getPriority(b.status);
    if (aPriority === bPriority) {
      return a.id.localeCompare(b.id);
    }
    return aPriority - bPriority;
  });
};

// helper to sum all four counts when no diffCategory is selected
export function sumAllCounts(m: DiffExecutionModelResult): number {
  return (
    (m.number_of_same_models ?? 0) +
    (m.number_of_different_models ?? 0) +
    (m.number_of_net_new_models ?? 0) +
    (m.number_of_net_missing_models ?? 0)
  );
}

// combines the published and candidate JSON into one labeled string for copying
export function buildCopyBothText(
  publishedString: string,
  candidateString: string,
  candidateTitle: string
): string {
  return `=== Published ===\n${publishedString}\n\n=== ${candidateTitle} ===\n${candidateString}`;
}

// map diff type → the matching field on the modelResult
export const diffTypeToCountKey: Record<DiffCategoryTypes, keyof DiffExecutionModelResult> = {
  [DiffCategoryTypes.DIFFERENT]: "number_of_different_models",
  [DiffCategoryTypes.NET_MISSING]: "number_of_net_missing_models",
  [DiffCategoryTypes.NET_NEW]: "number_of_net_new_models",
  [DiffCategoryTypes.SAME]: "number_of_same_models",
};

/**
 * Always returns a number, picking either the
 * single-diff count or the sum of all counts.
 */
export function computeCount(
  modelResult: DiffExecutionModelResult,
  diffCategory?: DiffCategoryTypes
): number {
  if (diffCategory) {
    const key = diffTypeToCountKey[diffCategory];
    const val = modelResult[key] as number | undefined;
    return val ?? 0;
  }
  return sumAllCounts(modelResult);
}
